import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { TemplateIcon } from '../../utils/iconResolver';

import {
  ArrowSquareOut,
  CaretDown,
  CaretUp,
  CheckCircle,
  Clock,
  Eye,
  FileText,
  Shield,
  Stack,
  Tag,
  User,
  Warning,
  XCircle
} from '@phosphor-icons/react';
const STATUS_BADGE = {
  pending_review: { label: 'Pending Review', color: 'text-yellow-400 bg-yellow-400/10' },
  changes_requested: { label: 'Changes Requested', color: 'text-orange-400 bg-orange-400/10' },
  approved: { label: 'Approved', color: 'text-green-400 bg-green-400/10' },
  rejected: { label: 'Rejected', color: 'text-red-400 bg-red-400/10' },
};

const AdminSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [changesReason, setChangesReason] = useState('');

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`${BACKEND_URL}/api/admin/submissions?${params}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubmissions(); }, [statusFilter]);

  const fetchDetail = async (templateId) => {
    if (detailCache[templateId]) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/submissions/${templateId}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setDetailCache(prev => ({ ...prev, [templateId]: data.submission }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAction = async (templateId, action, body = {}) => {
    setActionLoading(`${templateId}-${action}`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/submissions/${templateId}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        await fetchSubmissions();
        setExpandedId(null);
        setRejectReason('');
        setChangesReason('');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      fetchDetail(id);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-submissions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Automation Submissions</h1>
          <p className="text-[#8b8fa3] text-sm mt-1">{total} submissions</p>
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-sm"
          data-testid="submissions-filter"
        >
          <option value="">Pending + Changes Requested</option>
          <option value="pending_review">Pending Review</option>
          <option value="changes_requested">Changes Requested</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : submissions.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center">
          <CheckCircle size={40} className="text-green-400 mx-auto mb-3" />
          <p className="text-white font-medium">No pending submissions</p>
          <p className="text-[#8b8fa3] text-sm mt-1">All caught up!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map(sub => {
            const badge = STATUS_BADGE[sub.review_status] || STATUS_BADGE.pending_review;
            const isExpanded = expandedId === sub.id;
            const detail = detailCache[sub.id];

            return (
              <div key={sub.id} className="glass rounded-xl overflow-hidden" data-testid={`submission-${sub.id}`}>
                {/* Summary Row */}
                <button
                  onClick={() => toggleExpand(sub.id)}
                  className="w-full p-4 flex items-center gap-4 text-left hover:bg-[rgba(255,255,255,0.02)] transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-[rgba(99,102,241,0.12)] flex items-center justify-center text-lg shrink-0">
                    <TemplateIcon icon={sub.icon} size={22} className="text-indigo-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-medium text-sm truncate">{sub.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${badge.color}`}>{badge.label}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-[#6b7080] mt-0.5">
                      <span className="flex items-center gap-1"><User size={10} /> {sub.creator_display_name || sub.creator_username || 'Unknown'}</span>
                      <span className="flex items-center gap-1"><Stack size={10} /> {sub.category}</span>
                      <span>{sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : ''}</span>
                    </div>
                  </div>
                  {isExpanded ? <CaretUp size={16} className="text-[#555]" /> : <CaretDown size={16} className="text-[#555]" />}
                </button>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className="border-t border-[rgba(255,255,255,0.08)] p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-[#8b8fa3] text-xs mb-1">Description</p>
                        <p className="text-white text-sm">{sub.tagline || sub.short_description || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[#8b8fa3] text-xs mb-1">Tags</p>
                        <div className="flex flex-wrap gap-1">
                          {(sub.tags || []).map((t, i) => (
                            <span key={i} className="px-2 py-0.5 rounded bg-[rgba(255,255,255,0.05)] text-[#8b8fa3] text-[10px]">{t}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-[#8b8fa3] text-xs mb-1">Inputs</p>
                        <p className="text-white text-xs">{(sub.input_types || []).join(', ') || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[#8b8fa3] text-xs mb-1">Outputs</p>
                        <p className="text-white text-xs">{(sub.output_types || []).join(', ') || '—'}</p>
                      </div>
                    </div>

                    {/* Risk Flags */}
                    {detail?.risk_flags?.length > 0 && (
                      <div className="rounded-lg bg-[rgba(237,137,54,0.08)] border border-[rgba(237,137,54,0.2)] p-3" data-testid="risk-flags">
                        <p className="text-orange-400 text-xs font-semibold flex items-center gap-1.5 mb-2">
                          <Shield size={12} /> Security Flags
                        </p>
                        <ul className="space-y-1">
                          {detail.risk_flags.map((f, i) => (
                            <li key={i} className="text-[#A0AEC0] text-xs flex items-start gap-1.5">
                              <Warning size={10} className="text-orange-400 mt-0.5 shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Admin Actions */}
                    <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[rgba(255,255,255,0.08)]">
                      {sub.review_status !== 'approved' && (
                        <button
                          onClick={() => handleAction(sub.id, 'approve')}
                          disabled={!!actionLoading}
                          className="px-4 py-2 rounded-lg bg-green-500/10 text-green-400 text-xs font-medium hover:bg-green-500/20 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                          data-testid={`approve-${sub.id}`}
                        >
                          <CheckCircle size={14} /> Approve
                        </button>
                      )}
                      {sub.review_status !== 'rejected' && (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                            placeholder="Reason..."
                            className="px-3 py-1.5 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-xs w-40"
                          />
                          <button
                            onClick={() => handleAction(sub.id, 'reject', { reason: rejectReason })}
                            disabled={!!actionLoading}
                            className="px-4 py-2 rounded-lg bg-red-500/10 text-red-400 text-xs font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                            data-testid={`reject-${sub.id}`}
                          >
                            <XCircle size={14} /> Reject
                          </button>
                        </div>
                      )}
                      {sub.review_status !== 'approved' && (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={changesReason}
                            onChange={e => setChangesReason(e.target.value)}
                            placeholder="Feedback..."
                            className="px-3 py-1.5 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-xs w-40"
                          />
                          <button
                            onClick={() => handleAction(sub.id, 'changes', { reason: changesReason })}
                            disabled={!!actionLoading}
                            className="px-4 py-2 rounded-lg bg-orange-500/10 text-orange-400 text-xs font-medium hover:bg-orange-500/20 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                            data-testid={`changes-${sub.id}`}
                          >
                            <Warning size={14} /> Request Changes
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminSubmissions;
